// DataTable — la tabla del mock como componente: encabezado pegajoso, columnas declaradas y los
// estados de cargando, sin filas y sin fuente. Los anchos viven en un `<colgroup>`, así el
// encabezado y su celda no pueden quedar en anchos distintos.
import type { ReactElement, ReactNode } from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { Skeleton } from '@/kit';
import { cn } from '@/lib/cn';

export type SortDirection = 'asc' | 'desc';

export interface DataTableSort<TSortKey extends string = string> {
    key: TSortKey | null;
    direction: SortDirection;
}

/** Qué hace la celda con el texto que no entra en su ancho. */
export type DataTableCellOverflow = 'truncate' | 'nowrap' | 'wrap';

export interface DataTableColumn<TRow, TSortKey extends string = string> {
    key: string;
    label: ReactNode;
    /** Ancho tal cual va al `<col>`: un porcentaje reparte la banda, un `px` la fija. */
    width?: string;
    /** Cifras: encabezado y celda a la derecha, con dígitos de ancho parejo. */
    numeric?: boolean;
    /** Con `sortKey` el encabezado es un botón que ordena por esa clave. */
    sortKey?: TSortKey;
    /** Qué mide la columna, o por qué no tiene fuente. Va al `title` del encabezado. */
    note?: string;
    /**
     * `truncate` (el default) corta con puntos suspensivos; `nowrap` deja desbordar, que es lo que
     * pide una celda de chip para que no salga partido; `wrap` no impone nada.
     */
    cellOverflow?: DataTableCellOverflow;
    cell: (row: TRow) => ReactNode;
    /** El motivo de ESA celda, para su propio `title`. */
    cellTitle?: (row: TRow) => string | undefined;
    cellClassName?: (row: TRow) => string | undefined;
}

/** El `th` del mock (`.c-table`), con el mismo aire horizontal que su celda. */
const HEADER_CELL =
    'bg-muted px-2 py-1.5 align-middle text-[9.5px] font-bold uppercase leading-[1.5] tracking-[0.04em] text-muted-foreground';

/** El borde bajo el encabezado lo lleva la fila, no cada `th`. */
const HEADER_ROW = 'border-b border-border';

/**
 * El `td` en UNA línea: el texto que no entra se corta con puntos suspensivos. Así toda fila mide
 * lo mismo y la tabla se recorre de un vistazo. El encabezado sí parte en dos, que no cambia el alto.
 */
const BODY_CELL = 'border-b border-border/50 px-2 py-[7px] align-middle text-[11px]';

const CELL_OVERFLOW: Record<DataTableCellOverflow, string> = {
    truncate: 'truncate',
    nowrap: 'whitespace-nowrap',
    wrap: '',
};

/** La densidad `compact` es la de la tabla de Cobranza: encabezado de 9px y celdas de 11.5px. */
export type DataTableDensity = 'regular' | 'compact';

const HEADER_CELL_BY_DENSITY: Record<DataTableDensity, string> = {
    regular: HEADER_CELL,
    // El fondo tenue va sobre `bg-card`: el encabezado es pegajoso y un fondo translúcido dejaría ver las filas.
    compact:
        'overflow-hidden border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))] px-1.5 py-1 align-middle text-[9px] font-semibold uppercase leading-[1.5] tracking-[0.45px] text-muted-foreground',
};

const BODY_CELL_BY_DENSITY: Record<DataTableDensity, string> = {
    regular: BODY_CELL,
    compact: 'border-b border-border/60 px-1.5 py-2 align-middle text-[11.5px]',
};

const DEFAULT_SKELETON_ROWS = 10;

export interface DataTableProps<TRow, TSortKey extends string = string> {
    columns: DataTableColumn<TRow, TSortKey>[];
    /** Filas YA filtradas y ordenadas: la tabla pinta, no decide. `null` = no hay de dónde leerlas. */
    rows: readonly TRow[] | null;
    rowKey: (row: TRow) => string;
    sort?: DataTableSort<TSortKey>;
    /** Recibe la `sortKey` del encabezado clickeado; el sentido lo decide quien llama. */
    onSortToggle?: (sortKey: TSortKey) => void;
    onRowClick?: (row: TRow) => void;
    rowTitle?: (row: TRow) => string | undefined;
    /** Clases extra de la fila, p. ej. la marcada para un lote. */
    rowClassName?: (row: TRow) => string | undefined;
    isRowSelected?: (row: TRow) => boolean;
    /** Lo que se despliega bajo la fila, a todo el ancho; `null` la deja cerrada. */
    renderExpansion?: (row: TRow) => ReactElement | null;
    /** Mientras carga se pintan filas de esqueleto, para que la banda no salte al llegar el dato. */
    isLoading?: boolean;
    skeletonRows?: number;
    /** Qué decir cuando la lectura terminó y no hay ni una fila. */
    empty?: ReactNode;
    /**
     * Qué pintar EN LUGAR de la tabla cuando `rows` es `null`. Una tabla vacía diría «no hay
     * registros», y sin fuente eso no se sabe.
     */
    unavailable?: ReactNode;
    /** Qué contiene la tabla, para quien la lee con lector de pantalla. */
    caption?: ReactNode;
    /** Ancho por debajo del cual la banda scrollea en vez de aplastar las columnas. */
    minWidth?: string;
    density?: DataTableDensity;
    className?: string;
}

export function DataTable<TRow, TSortKey extends string = string>({
    columns,
    rows,
    rowKey,
    sort,
    onSortToggle,
    onRowClick,
    rowTitle,
    rowClassName,
    isRowSelected,
    renderExpansion,
    isLoading = false,
    skeletonRows = DEFAULT_SKELETON_ROWS,
    empty,
    unavailable,
    caption,
    minWidth,
    density = 'regular',
    className,
}: DataTableProps<TRow, TSortKey>) {
    // Sin fuente no se pinta tabla: el motivo ocupa la banda entera, sin el `<table>` alrededor.
    if (rows === null) return <>{unavailable}</>;

    const showEmpty = !isLoading && rows.length === 0 && empty !== undefined;

    return (
        <div className={cn('min-h-0 flex-1 overflow-auto', className)}>
            <table className="w-full table-fixed" style={minWidth ? { minWidth } : undefined}>
                {caption !== undefined ? <caption className="sr-only">{caption}</caption> : null}
                <colgroup>
                    {columns.map((col) => (
                        <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                    ))}
                </colgroup>
                <thead className="sticky top-0 z-10">
                    <tr className={HEADER_ROW}>
                        {columns.map((col) => {
                            // Una columna ordena solo si declara clave Y hay quien escuche el click.
                            const sortKey = onSortToggle ? col.sortKey : undefined;
                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    title={col.note}
                                    aria-sort={
                                        sortKey === undefined
                                            ? undefined
                                            : ariaSortOf(sort, sortKey)
                                    }
                                    className={cn(
                                        // El fondo va en el `th`, como el mock: en el `thead` no lo pinta.
                                        HEADER_CELL_BY_DENSITY[density],
                                        col.numeric ? 'text-right' : 'text-left',
                                        col.note && 'cursor-help',
                                    )}
                                >
                                    {sortKey !== undefined && onSortToggle ? (
                                        <SortButton
                                            label={col.label}
                                            sortKey={sortKey}
                                            numeric={col.numeric}
                                            quietCaret={density === 'compact'}
                                            sort={sort}
                                            onSortToggle={onSortToggle}
                                        />
                                    ) : (
                                        col.label
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {isLoading
                        ? Array.from({ length: skeletonRows }, (_unused, index) => (
                              <tr key={index}>
                                  {columns.map((col) => (
                                      <td
                                          key={col.key}
                                          className={cn(
                                              BODY_CELL_BY_DENSITY[density],
                                              CELL_OVERFLOW.truncate,
                                          )}
                                      >
                                          <Skeleton className="h-3" />
                                      </td>
                                  ))}
                              </tr>
                          ))
                        : rows.map((row) => (
                              <DataTableRow
                                  key={rowKey(row)}
                                  row={row}
                                  columns={columns}
                                  title={rowTitle?.(row)}
                                  className={rowClassName?.(row)}
                                  bodyCell={BODY_CELL_BY_DENSITY[density]}
                                  selected={isRowSelected?.(row) ?? false}
                                  onRowClick={onRowClick}
                                  expansion={renderExpansion?.(row) ?? null}
                              />
                          ))}
                </tbody>
            </table>
            {showEmpty ? (
                <div className="py-8 text-center text-[12px] text-muted-foreground">{empty}</div>
            ) : null}
        </div>
    );
}

/** Cómo anuncia el encabezado su orden. `none` dice «se puede ordenar, pero no ordena hoy». */
function ariaSortOf<TSortKey extends string>(
    sort: DataTableSort<TSortKey> | undefined,
    sortKey: TSortKey,
): 'ascending' | 'descending' | 'none' {
    if (sort === undefined || sort.key !== sortKey) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
}

function DataTableRow<TRow, TSortKey extends string>({
    row,
    columns,
    title,
    className,
    bodyCell,
    selected,
    onRowClick,
    expansion,
}: {
    row: TRow;
    columns: DataTableColumn<TRow, TSortKey>[];
    title: string | undefined;
    className: string | undefined;
    bodyCell: string;
    selected: boolean;
    onRowClick: ((row: TRow) => void) | undefined;
    expansion: ReactElement | null;
}) {
    return (
        <>
            <tr
                title={title}
                aria-selected={selected}
                onClick={
                    onRowClick
                        ? () => {
                              onRowClick(row);
                          }
                        : undefined
                }
                className={cn(
                    onRowClick && 'cursor-pointer',
                    selected
                        ? 'bg-success-soft/55 shadow-[inset_3px_0_0_hsl(var(--success))] hover:bg-success-soft/70'
                        : onRowClick && 'hover:bg-accent/40',
                    className,
                )}
            >
                {columns.map((col) => (
                    <td
                        key={col.key}
                        title={col.cellTitle?.(row)}
                        className={cn(
                            bodyCell,
                            CELL_OVERFLOW[col.cellOverflow ?? 'truncate'],
                            col.numeric && 'text-right tabular-nums',
                            col.cellClassName?.(row),
                        )}
                    >
                        {col.cell(row)}
                    </td>
                ))}
            </tr>
            {expansion !== null ? (
                <tr>
                    <td colSpan={columns.length} className="border-b border-border/50 px-2 py-2">
                        {expansion}
                    </td>
                </tr>
            ) : null}
        </>
    );
}

function SortButton<TSortKey extends string>({
    label,
    sortKey,
    numeric,
    quietCaret = false,
    sort,
    onSortToggle,
}: {
    label: ReactNode;
    sortKey: TSortKey;
    numeric: boolean | undefined;
    /** La flecha de la columna sin ordenar solo aparece al pasar el cursor. */
    quietCaret?: boolean;
    sort: DataTableSort<TSortKey> | undefined;
    onSortToggle: (sortKey: TSortKey) => void;
}) {
    const direction = sort?.key === sortKey ? sort.direction : null;
    const active = direction !== null;
    // En cifras la flecha flota a la izquierda: a la derecha la recorta el borde del `th`.
    const outsideCaret = numeric ? 'right-full' : 'left-full';
    return (
        <button
            type="button"
            onClick={() => {
                onSortToggle(sortKey);
            }}
            // `uppercase` va acá y no solo en el `th`: el preflight de Tailwind resetea el
            // `text-transform` de todo `button`, así que el del encabezado no llegaba al label.
            className={cn(
                'group relative inline-flex max-w-full select-none items-center gap-0.5 uppercase transition-colors hover:text-foreground',
                // En una columna de cifras la flecha va del lado de adentro, o el título queda
                // corrido a la izquierda del número con el que tiene que alinearse.
                numeric && 'flex-row-reverse',
                active && 'text-foreground',
            )}
        >
            <span className="truncate">{label}</span>
            {/* La columna sin ordenar lleva la doble flecha tenue: dice que se puede ordenar por
                ella sin gritar tanto como la que ya ordena. */}
            {!active ? (
                quietCaret ? (
                    <ChevronDown
                        className={cn(
                            'absolute size-2.5 opacity-0 transition-opacity group-hover:opacity-40',
                            outsideCaret,
                        )}
                        aria-hidden
                    />
                ) : (
                    <ChevronsUpDown
                        className="size-2.5 flex-none opacity-30 transition-opacity group-hover:opacity-70"
                        aria-hidden
                    />
                )
            ) : direction === 'asc' ? (
                <ChevronUp
                    className={cn('size-2.5 flex-none', quietCaret && ['absolute', outsideCaret])}
                    aria-hidden
                />
            ) : (
                <ChevronDown
                    className={cn('size-2.5 flex-none', quietCaret && ['absolute', outsideCaret])}
                    aria-hidden
                />
            )}
        </button>
    );
}
